/**
 * Popup Channel - Unified Communication Layer
 * 
 * Single BroadcastChannel for all popup ↔ main window communication.
 * Main window holds WebSocket connections; popups are stateless views.
 * 
 * See README.md for architecture documentation.
 */

import { useEffect, useCallback, useRef } from 'react';

// Single channel for all communication
const CHANNEL_NAME = 'ais_popup';

// ============================================================================
// Message Types
// ============================================================================

export enum MessageType {
  // Popup → Main: Lifecycle
  POPUP_INIT = 'POPUP_INIT',
  POPUP_CLOSED = 'POPUP_CLOSED',
  POPUP_HEARTBEAT = 'POPUP_HEARTBEAT',
  
  // Main → Popup: Heartbeat acknowledgment
  HEARTBEAT_ACK = 'HEARTBEAT_ACK',
  
  // Popup → Main: Actions (fire-and-forget)
  VIEW_TARGET = 'VIEW_TARGET',
  ZOOM_TO_TARGET = 'ZOOM_TO_TARGET',
  ZOOM_TO_COORDINATES = 'ZOOM_TO_COORDINATES',
  ZOOM_TO_PORT = 'ZOOM_TO_PORT',
  EXPORT_REPORT = 'EXPORT_REPORT',
  RERUN_REPORT = 'RERUN_REPORT',
  REQUEST_TARGETS_REFRESH = 'REQUEST_TARGETS_REFRESH',
  
  // Event actions
  GOTO_EVENT = 'GOTO_EVENT',
  SHOW_EVENT_SHAPE = 'SHOW_EVENT_SHAPE',
  ACKNOWLEDGE_EVENT = 'ACKNOWLEDGE_EVENT',
  SET_EVENT_DATE_RANGE = 'SET_EVENT_DATE_RANGE',
  
  // Popup → Main: Requests (expect response)
  REQUEST_DATA = 'REQUEST_DATA',
  
  // Main → Popup: Responses
  POPUP_INIT_RESPONSE = 'POPUP_INIT_RESPONSE',
  DATA_RESPONSE = 'DATA_RESPONSE',
  
  // Main → Popup: Push updates
  DATA_UPDATE = 'DATA_UPDATE',
  SESSION_ENDED = 'SESSION_ENDED',
}

// ============================================================================
// Message Structure
// ============================================================================

export interface PopupMessage<T = unknown> {
  /** Unique ID for request/response correlation */
  id: string;
  /** Message type */
  type: MessageType;
  /** Target popup ID (for directed messages) */
  popupId?: string;
  /** Message payload */
  payload: T;
  /** Origin */
  source: 'main' | 'popup';
  /** Unix timestamp */
  timestamp: number;
}

// ============================================================================
// Payload Types
// ============================================================================

export interface PopupInitPayload {
  popupId: string;
  popupType: string;
  params: Record<string, unknown>;
}

export interface ViewTargetPayload {
  mmsi?: string;
  imo?: string;
  name?: string;
  callSign?: string;
}

export interface ZoomToCoordinatesPayload {
  lat: number;
  lon: number;
  zoom?: number;
}

export interface ZoomToPortPayload {
  locode: string;
}

export interface ExportReportPayload {
  jobId: string;
  format: string;
}

export interface GotoEventPayload {
  eventId: string;
}

export interface ShowEventShapePayload {
  shapeUid: string;
  detectorName: string;
}

export interface AcknowledgeEventPayload {
  eventId: string;
}

export interface SetEventDateRangePayload {
  from: string;
  to: string;
  isTrailing: boolean;
}

// ============================================================================
// Data Types - IMPORTANT: Use these constants for dataType in DATA_UPDATE
// ============================================================================

/**
 * Standardized data type keys for DATA_UPDATE messages.
 * Always use these constants - never use string literals directly.
 * 
 * Adding a new data type:
 * 1. Add it to this enum
 * 2. Add the payload type to DataPayloadMap
 * 3. Update README.md with the new type
 */
export enum DataType {
  /** Ship data for reports (array of ReportShipData) */
  SHIP_DATA = 'shipData',
  /** Port timeline events */
  PORT_TIMELINE = 'portTimeline',
  /** Settings/preferences updates */
  SETTINGS = 'settings',
  /** Targets on map data */
  TARGETS = 'targets',
  /** Events data with detector counts */
  EVENTS = 'events',
  /** Ship track data for vessel timeline chart */
  SHIP_TRACK = 'shipTrack',
}

/** Map data types to their payload shapes for type safety */
export interface DataPayloadMap {
  [DataType.SHIP_DATA]: { data: unknown[]; error?: string };
  [DataType.PORT_TIMELINE]: { events: unknown[]; error?: string };
  [DataType.SETTINGS]: { 
    density?: string; 
    theme?: string;
    dateFormat?: string;
    timeZone?: string;
  };
  [DataType.TARGETS]: { targets: unknown[]; total: number; retrievalTime: string; bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number } };
  [DataType.EVENTS]: { events: unknown[]; totalEvents: number; activeEvents: number; deactivatedEvents: number; dateRange: { from: string; to: string }; isTrailing: boolean; settings?: { tableDensity: 'normal' | 'compact' }; dataKey: string };
  [DataType.SHIP_TRACK]: { shipTrack: { coordinates: number[][] } | null; chartDataKey: string; error?: string };
}

export interface DataUpdatePayload<T extends DataType = DataType> {
  dataType: T;
  data: DataPayloadMap[T];
}

// ============================================================================
// Core Channel Functions
// ============================================================================

let messageIdCounter = 0;
const generateId = (): string => `${Date.now()}-${++messageIdCounter}`;

// Singleton channel for sending messages (reused across all sends)
let sendChannel: BroadcastChannel | null = null;

const getSendChannel = (): BroadcastChannel | null => {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!sendChannel) {
    sendChannel = new BroadcastChannel(CHANNEL_NAME);
  }
  return sendChannel;
};

/**
 * Send a message on the popup channel
 */
export const sendMessage = <T>(
  type: MessageType,
  payload: T,
  options: { popupId?: string; source: 'main' | 'popup' }
): string => {
  const id = generateId();
  const channel = getSendChannel();
  if (channel) {
    const message: PopupMessage<T> = {
      id,
      type,
      popupId: options.popupId,
      payload,
      source: options.source,
      timestamp: Date.now(),
    };
    channel.postMessage(message);
  }
  return id;
};

/**
 * Subscribe to messages on the popup channel
 * Each subscription creates its own channel for independent listener lifecycle
 */
export const subscribe = (
  callback: (message: PopupMessage) => void,
  filter?: { source?: 'main' | 'popup'; popupId?: string }
): (() => void) => {
  if (typeof BroadcastChannel === 'undefined') {
    return () => {};
  }
  
  const channel = new BroadcastChannel(CHANNEL_NAME);
  
  const handler = (e: MessageEvent<PopupMessage>) => {
    const msg = e.data;
    if (!msg?.type) return;
    
    // Apply filters
    if (filter?.source && msg.source !== filter.source) {
      return;
    }
    // Only filter by popupId if BOTH the filter AND the message have a popupId
    // This allows broadcast messages (no popupId) to pass through to all listeners
    if (filter?.popupId && msg.popupId && msg.popupId !== filter.popupId) {
      return;
    }
    
    callback(msg);
  };
  
  channel.addEventListener('message', handler);
  return () => {
    channel.removeEventListener('message', handler);
    channel.close();
  };
};

// ============================================================================
// Popup Actions (used by popup windows)
// ============================================================================

export const PopupActions = {
  /** Request initial data when popup opens */
  requestInit: (payload: PopupInitPayload): string => {
    return sendMessage(MessageType.POPUP_INIT, payload, { source: 'popup', popupId: payload.popupId });
  },

  /** Notify main window popup is closing */
  notifyClosed: (popupId: string): void => {
    sendMessage(MessageType.POPUP_CLOSED, { popupId }, { source: 'popup', popupId });
  },

  /** Send heartbeat to keep popup registered as active */
  heartbeat: (popupId: string, popupType: string): void => {
    sendMessage(MessageType.POPUP_HEARTBEAT, { popupId, popupType }, { source: 'popup', popupId });
  },

  /** Request main window to view/select a target */
  viewTarget: (payload: ViewTargetPayload): void => {
    sendMessage(MessageType.VIEW_TARGET, payload, { source: 'popup' });
  },

  /** Request main window to zoom to a target */
  zoomToTarget: (payload: ViewTargetPayload): void => {
    sendMessage(MessageType.ZOOM_TO_TARGET, payload, { source: 'popup' });
  },

  /** Request main window to zoom to coordinates */
  zoomToCoordinates: (payload: ZoomToCoordinatesPayload): void => {
    sendMessage(MessageType.ZOOM_TO_COORDINATES, payload, { source: 'popup' });
  },

  /** Request main window to zoom to a port by locode */
  zoomToPort: (payload: ZoomToPortPayload): void => {
    sendMessage(MessageType.ZOOM_TO_PORT, payload, { source: 'popup' });
  },

  /** Request report export */
  exportReport: (payload: ExportReportPayload): void => {
    sendMessage(MessageType.EXPORT_REPORT, payload, { source: 'popup' });
  },

  /** Request report re-run */
  rerunReport: (jobId: string): void => {
    sendMessage(MessageType.RERUN_REPORT, { jobId }, { source: 'popup' });
  },

  /** Request specific data from main window */
  requestData: (popupId: string, dataType: string, params?: Record<string, unknown>): string => {
    return sendMessage(MessageType.REQUEST_DATA, { dataType, params }, { source: 'popup', popupId });
  },

  /** Go to event location on map */
  gotoEvent: (eventId: string): void => {
    sendMessage(MessageType.GOTO_EVENT, { eventId }, { source: 'popup' });
  },

  /** Show event detector shape on map */
  showEventShape: (shapeUid: string, detectorName: string): void => {
    sendMessage(MessageType.SHOW_EVENT_SHAPE, { shapeUid, detectorName }, { source: 'popup' });
  },

  /** Acknowledge an event */
  acknowledgeEvent: (eventId: string): void => {
    sendMessage(MessageType.ACKNOWLEDGE_EVENT, { eventId }, { source: 'popup' });
  },

  /** Set event date range (syncs with main window EventList) */
  setEventDateRange: (from: string, to: string, isTrailing: boolean): void => {
    sendMessage(MessageType.SET_EVENT_DATE_RANGE, { from, to, isTrailing }, { source: 'popup' });
  },
};

// ============================================================================
// Main Window Actions (used by main application)
// ============================================================================

export const MainWindowActions = {
  /** Respond to popup init request with data */
  sendInitResponse: (popupId: string, payload: unknown): void => {
    sendMessage(MessageType.POPUP_INIT_RESPONSE, payload, { source: 'main', popupId });
  },

  /** Respond to data request */
  sendDataResponse: (popupId: string, requestId: string, data: unknown): void => {
    sendMessage(MessageType.DATA_RESPONSE, { requestId, data }, { source: 'main', popupId });
  },

  /** Push data update to all popups (or specific popup) */
  sendDataUpdate: <T extends DataType>(dataType: T, data: DataPayloadMap[T] | null, popupId?: string, isLoading?: boolean): void => {
    sendMessage(MessageType.DATA_UPDATE, { dataType, data, isLoading }, { source: 'main', popupId });
  },

  /** Notify all popups session ended */
  sessionEnded: (reason: string): void => {
    sendMessage(MessageType.SESSION_ENDED, { reason }, { source: 'main' });
  },

  /** Acknowledge heartbeat from popup */
  sendHeartbeatAck: (popupId: string): void => {
    sendMessage(MessageType.HEARTBEAT_ACK, { popupId }, { source: 'main', popupId });
  },
};

// ============================================================================
// React Hooks
// ============================================================================

export type MessageCallback = (message: PopupMessage) => void;

/**
 * Hook to listen for messages (main window listens for popup messages)
 */
export const usePopupMessages = (
  callback: MessageCallback,
  deps: React.DependencyList = []
): void => {
  useEffect(() => {
    return subscribe(callback, { source: 'popup' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

/**
 * Hook to listen for messages from main window (popup listens for main messages)
 */
export const useMainWindowMessages = (
  callback: MessageCallback,
  popupId?: string,
  deps: React.DependencyList = []
): void => {
  useEffect(() => {
    return subscribe(callback, { source: 'main', popupId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, popupId]);
};

/**
 * Hook for request/response pattern with timeout
 */
export const usePopupRequest = () => {
  const pendingRequests = useRef<Map<string, (data: unknown) => void>>(new Map());

  useEffect(() => {
    return subscribe((msg) => {
      if (msg.type === MessageType.DATA_RESPONSE) {
        const payload = msg.payload as { requestId: string; data: unknown };
        const resolver = pendingRequests.current.get(payload.requestId);
        if (resolver) {
          resolver(payload.data);
          pendingRequests.current.delete(payload.requestId);
        }
      }
    }, { source: 'main' });
  }, []);

  const request = useCallback(async <T>(
    popupId: string,
    dataType: string,
    params?: Record<string, unknown>,
    timeoutMs = 5000
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const requestId = PopupActions.requestData(popupId, dataType, params);
      
      const timeout = setTimeout(() => {
        pendingRequests.current.delete(requestId);
        reject(new Error(`Request timeout: ${dataType}`));
      }, timeoutMs);

      pendingRequests.current.set(requestId, (data) => {
        clearTimeout(timeout);
        resolve(data as T);
      });
    });
  }, []);

  return { request };
};
